import { navigate } from 'gatsby';
import React from 'react';

import PageLoader from '@components/PageLoader';

import { HANDOFF_FAILED } from '@constants/error-codes';
import * as ROUTES from '@constants/routes';

import { useSession } from '@hooks/useSession';

/**
 * Takes the single-use handoff nonce out of the URL fragment and removes it
 * from history. Scan mints the nonce in a fragment rather than a query string
 * so it never reaches a server log, and stripping it here keeps it out of the
 * back button, bookmarks and anything the user shares.
 */
function takeTokenFromFragment(): string | null {
  const token = new URLSearchParams(window.location.hash.slice(1)).get('t');
  window.history.replaceState(null, '', window.location.pathname);
  return token;
}

/**
 * Trades a handoff nonce from VITALL Scan for a web session, so a user already
 * signed in on the native app lands here logged in.
 */
function SsoHandoff() {
  const { handoff } = useSession();

  // The nonce is spent by the first attempt whether or not it succeeds, so a
  // second run of this effect could only ever burn a token that is already
  // gone. React StrictMode double-invokes effects in development.
  const claimed = React.useRef(false);

  const failToLogin = () =>
    navigate(ROUTES.LOGIN, { state: { redirect: HANDOFF_FAILED } });

  React.useEffect(() => {
    if (claimed.current) {
      return;
    }
    claimed.current = true;

    const token = takeTokenFromFragment();
    if (!token) {
      failToLogin();
      return;
    }

    // A failed handoff is an absent session rather than a broken app, so the
    // login screen is always a valid place to end up.
    handoff(token).catch(failToLogin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <PageLoader />;
}

export default SsoHandoff;
